import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { Paper, Typography, Collapse, IconButton } from '@mui/material';
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot, TimelineOppositeContent } from '@mui/lab';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const PREFIX = 'EducationSection';
const classes = {
  educationContainer: `${PREFIX}-educationContainer`,
  paperContainer: `${PREFIX}-paperContainer`,
  institution: `${PREFIX}-institution`,
  degree: `${PREFIX}-degree`,
  timelineOpposite: `${PREFIX}-timelineOpposite`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.educationContainer}`]: {
    padding: theme.spacing(4),
  },
  [`& .${classes.paperContainer}`]: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: `${theme.palette.background.paper}CC`,
    boxShadow: `0px 4px 20px ${theme.palette.grey[300]}`,
    border: `1px solid ${theme.palette.divider}`,
    position: 'relative',
    cursor: 'pointer',
    paddingRight: theme.spacing(7), // Add padding to avoid overlap with the expand icon
  },
  [`& .${classes.institution}`]: {
    fontSize: '1.25rem',
    fontWeight: '600',
    fontStyle: 'normal',
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(0.5),
    overflow: 'hidden', // Ensure text doesn't overflow
    whiteSpace: 'nowrap', // No wrap onto the next line
    textOverflow: 'ellipsis', // Add an ellipsis when text overflows
  },
  [`& .${classes.degree}`]: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  [`& .${classes.timelineOpposite}`]: {
    flex: 0.1,
  },
}));

const ExpandIcon = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(2),
  top: theme.spacing(2),
  color: theme.palette.text.secondary,
}));

const educationData = [
  {
    period: "2019 - 2024",
    institution: "Indian Institute of Technology, Varanasi",
    degree: "Integrated Dual Degree in Mathematics and Computing",
    details: "Embarking on a five-year college journey, I navigated through challenges and opportunities with a keen focus on growth beyond academia. Actively participating in outreach and cine club, I embraced community and creativity before the pandemic shifted my path towards development and startups, fostering invaluable skills in a remote learning environment. Maintaining an 8+ CPI, my academic journey was balanced with practical learning, including a deep dive into Data Structures and Algorithms (DSA) to sharpen my problem-solving skills upon returning to campus. This blend of academic diligence, extracurricular engagement, and self-directed learning in technology and problem-solving marks the essence of my transformative college experience, readying me for the professional world with a rich portfolio of knowledge and skills."
  },
  {
    period: "2016 - 2018",
    institution: "Central Hindu Boys School, Varanasi",
    degree: "Senior Secondary School",
    details: "During this time, my life was intensely focused on preparing for the JEE exam, dedicating two years to rigorous study in a hostel setting. This phase prioritized competitive exam preparation over regular school academics, fostering resilience, independence, and a strong work ethic as I navigated the challenges of living alone and managing a demanding schedule. This experience was transformative, teaching me valuable lessons in prioritization, self-discipline, and perseverance, and laying a solid foundation for my future endeavors."
  },
];

const EducationSection = () => {
  const [expanded, setExpanded] = useState(Array(educationData.length).fill(false));

  const handleExpandClick = (index) => {
    setExpanded(expanded.map((state, i) => (i === index ? !state : state)));
  };

  return (
    <Root className={classes.educationContainer} id="educationSection">
      <Timeline position="alternate">
        {educationData.map((entry, index) => (
          <TimelineItem key={index}>
            <TimelineOppositeContent className={classes.timelineOpposite}>
              <Typography variant="body2" color="textSecondary">{entry.period}</Typography>
            </TimelineOppositeContent>
            <TimelineSeparator>
              <TimelineDot />
              <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent>
              <Paper elevation={3} className={classes.paperContainer} onClick={() => handleExpandClick(index)}>
                <Typography className={classes.institution}>{entry.institution}</Typography>
                <Typography className={classes.degree}>{entry.degree}</Typography>
                <Collapse in={expanded[index]}>
                  <Typography variant="body2">
                    {entry.details}
                  </Typography>
                </Collapse>
                <ExpandIcon
                  onClick={(event) => {
                    event.stopPropagation(); // Prevents the PaperContainer onClick from being triggered again
                    handleExpandClick(index);
                  }}
                  aria-expanded={expanded[index]}
                  aria-label="Show more"
                >
                  <ExpandMoreIcon />
                </ExpandIcon>
              </Paper>
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>
    </Root>
  );
};

export default EducationSection;
